import {useMemo} from 'react'
import {Modal, StyleSheet, Text, View} from 'react-native'
import {useSafeAreaInsets} from 'react-native-safe-area-context'
import {Feather} from '@expo/vector-icons'
import {Trans} from '@lingui/react/macro'

import {useSession} from '#/state/session'
import {CapsuleThemePlayer} from '#/components/capsules/CapsuleThemePlayer'
import {type CapsuleModel, type Slide} from '#/types/capsule'

type Props = {
  visible: boolean
  slides: Slide[]
  themeId?: string
  htmlThemeUrl?: string
  title?: string
  primaryColor?: string
  onClose: () => void
}

export function CapsulePreviewModal({
  visible,
  slides,
  themeId = 'classic-quiz',
  htmlThemeUrl,
  title = '',
  onClose,
}: Props) {
  const insets = useSafeAreaInsets()
  const {currentAccount} = useSession()

  const capsuleModel: CapsuleModel = useMemo(
    () => ({
      id: 'preview',
      title,
      description: '',
      creatorId: currentAccount?.did ?? '',
      themeId,
      status: 'draft',
      slides,
      behaviorSettings: htmlThemeUrl ? {htmlThemeUrl} : {},
      metadata: {createdAt: '', updatedAt: ''},
      likeCount: 0,
      likedByMe: false,
    }),
    [title, themeId, htmlThemeUrl, slides, currentAccount?.did],
  )

  const previewDisclaimer = (
    <View style={ss.disclaimer}>
      <Feather name="eye" size={11} color="rgba(245,158,11,0.8)" />
      <Text style={ss.disclaimerText}>
        <Trans>Preview mode – no data is saved</Trans>
      </Text>
    </View>
  )

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="fullScreen"
      onRequestClose={onClose}>
      <View style={[ss.container, {paddingTop: insets.top}]}>
        <CapsuleThemePlayer
          capsule={capsuleModel}
          isPreview
          previewFooter={previewDisclaimer}
          insetsTop={insets.top}
          onClose={onClose}
        />
      </View>
    </Modal>
  )
}

const ss = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0a1220',
  },
  disclaimer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: 'rgba(245,158,11,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(245,158,11,0.2)',
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 5,
    alignSelf: 'center',
  },
  disclaimerText: {
    color: 'rgba(245,158,11,0.8)',
    fontSize: 11,
  },
})
